import { ActivitySquare, AlertCircle, CheckCircle, PauseCircle, Server as ServerIcon, BarChart2 } from "lucide-react";
import { useMemo, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@tremor/react";
import { useTranslation } from "react-i18next";
import Badge from "../components/Badge.jsx";
import { useMonitors } from "../queries/monitors.js";
import { fetchServers } from "../api/servers.js";
import { fetchRumWorkspaceSummary } from "../api/rum.js";
import { useIncidents, useIncidentAnalytics } from "../queries/incidents.js";
import { useChannels } from "../queries/channels.js";
import { useStatusPages } from "../queries/statusPages.js";
import { useMembers } from "../queries/workspaces.js";
import { fmtDate, fmtDateTime } from "../utils/format.js";
import {
  FirstRunHero,
  RecommendedMonitor,
  SetupProgress,
} from "../components/dashboard/Onboarding.jsx";

const STATUS_COLORS = {
  up: "#22c55e",
  degraded: "#f59e0b",
  down: "#ef4444",
  needs_attention: "#d97706",
  paused: "#94a3b8",
};

const SEVERITY_COLORS = {
  critical: "#dc2626",
  high: "#f97316",
  medium: "#eab308",
  low: "#0D9488",
};

const STATUS_BADGE_COLOR = {
  up: "emerald",
  degraded: "amber",
  down: "red",
  needs_attention: "amber",
  paused: "gray",
  open: "red",
  resolved: "emerald",
  acknowledged: "amber",
  investigating: "orange",
};

const SEVERITY_BADGE_COLOR = {
  critical: "red",
  high: "orange",
  medium: "yellow",
  low: "teal",
};

function StatCard({ label, value, Icon, color, subtitle }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <p className="mt-3 text-3xl font-semibold text-gray-900">{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    </Card>
  );
}

function Panel({ title, children }) {
  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      </div>
      <div className="px-5 py-5">{children}</div>
    </Card>
  );
}

function IncidentRow({ incident }) {
  const { t } = useTranslation("dashboard");
  const started = new Date(incident.started_at);
  const duration = incident.resolved_at
    ? t("duration.minutes", {
        count: Math.round((new Date(incident.resolved_at) - started) / 60000),
      })
    : t("duration.ongoing");

  return (
    <TableRow>
      <TableCell className="font-medium text-gray-900">
        {incident.monitor_name || incident.title}
      </TableCell>
      <TableCell className="text-gray-500">{fmtDateTime(started)}</TableCell>
      <TableCell className="text-gray-500">{duration}</TableCell>
      <TableCell>
        <Badge color={STATUS_BADGE_COLOR[incident.status] ?? "gray"}>
          {t(`status.${incident.status}`)}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge color={SEVERITY_BADGE_COLOR[incident.severity] ?? "gray"}>
          {t(`severity.${incident.severity}`)}
        </Badge>
      </TableCell>
    </TableRow>
  );
}

function isOnline(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 2 * 60 * 1000;
}

export default function Dashboard() {
  const { t } = useTranslation(["dashboard", "common"]);
  const [servers, setServers] = useState([]);
  const [rumSummary, setRumSummary] = useState(null);
  useEffect(() => { fetchServers().then(setServers).catch(() => {}); }, []);
  useEffect(() => { fetchRumWorkspaceSummary().then(setRumSummary).catch(() => {}); }, []);

  const monitorsQuery = useMonitors({ limit: 100 });
  const incidentsQuery = useIncidents({ limit: 12 });
  const analyticsQuery = useIncidentAnalytics();
  const channelsQuery = useChannels();
  const statusPagesQuery = useStatusPages();
  const membersQuery = useMembers();

  const monitors = monitorsQuery.data?.data ?? [];
  const incidents = incidentsQuery.data?.data ?? [];
  const analytics = analyticsQuery.data ?? null;
  const channels = channelsQuery.data ?? [];
  const statusPages = statusPagesQuery.data ?? [];
  const members = membersQuery.data ?? [];
  const loading =
    monitorsQuery.isPending || incidentsQuery.isPending || analyticsQuery.isPending;

  const alertingReady = channels.length > 0;

  const data = useMemo(() => {
    const totalMonitors = monitors.length;
    const activeMonitors = monitors.filter((monitor) => monitor.is_active).length;
    const pausedMonitors = totalMonitors - activeMonitors;
    const openIncidents = incidents.filter(
      (incident) => incident.status === "open"
    ).length;

    const statusBreakdown = [
      {
        name: t("status.up"),
        value: monitors.filter((m) => m.status === "up").length,
        color: STATUS_COLORS.up,
      },
      {
        name: t("status.degraded"),
        value: monitors.filter((m) => m.status === "degraded").length,
        color: STATUS_COLORS.degraded,
      },
      {
        name: t("status.down"),
        value: monitors.filter((m) => m.status === "down").length,
        color: STATUS_COLORS.down,
      },
      {
        name: t("status.needs_attention"),
        value: monitors.filter((m) => m.status === "needs_attention").length,
        color: STATUS_COLORS.needs_attention,
      },
      { name: t("status.paused"), value: pausedMonitors, color: STATUS_COLORS.paused },
    ].filter((item) => item.value > 0);

    const severityBreakdown = ["critical", "high", "medium", "low"].map((severity) => ({
      severity: t(`severity.${severity}`),
      count: incidents.filter((incident) => incident.severity === severity).length,
      color: SEVERITY_COLORS[severity],
    }));

    const trend = analytics?.daily_counts?.length
      ? analytics.daily_counts.map((item) => ({
          label: fmtDate(item.date),
          count: item.count,
        }))
      : [];

    const noisyMonitors = [...monitors]
      .filter(
        (monitor) =>
          monitor.status === "down" ||
          monitor.status === "degraded" ||
          monitor.status === "needs_attention"
      )
      .slice(0, 8);

    return {
      totalMonitors,
      activeMonitors,
      pausedMonitors,
      openIncidents,
      statusBreakdown,
      severityBreakdown,
      trend,
      noisyMonitors,
    };
  }, [analytics, incidents, monitors, t]);

  if (loading) {
    return <p className="text-sm text-gray-400">{t("common:loading")}</p>;
  }

  // First-run: a workspace with zero monitors gets a guided hero instead of
  // a grid of empty charts.
  if (data.totalMonitors === 0) {
    return <FirstRunHero />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
      </div>

      <SetupProgress
        monitorCount={data.totalMonitors}
        alertingReady={alertingReady}
        hasStatusPage={statusPages.length > 0}
        memberCount={members.length}
      />

      <RecommendedMonitor monitors={monitors} />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          label={t("stats.monitors")}
          value={data.totalMonitors}
          Icon={ActivitySquare}
          color="#0D9488"
        />
        <StatCard
          label={t("stats.active")}
          value={data.activeMonitors}
          Icon={CheckCircle}
          color="#16a34a"
        />
        <StatCard
          label={t("stats.paused")}
          value={data.pausedMonitors}
          Icon={PauseCircle}
          color="#64748b"
        />
        <StatCard
          label={t("stats.open_incidents")}
          value={data.openIncidents}
          Icon={AlertCircle}
          color={data.openIncidents > 0 ? "#dc2626" : "#94a3b8"}
        />
        {servers.length > 0 && (() => {
          const onlineCount = servers.filter((s) => s.is_active && isOnline(s.last_seen_at)).length;
          const alertingCount = servers.filter((s) => s.open_incidents > 0).length;
          return (
            <StatCard
              label={`Servers (${onlineCount}/${servers.length} online)`}
              value={alertingCount > 0 ? `${alertingCount} alerting` : "All clear"}
              Icon={ServerIcon}
              color={alertingCount > 0 ? "#dc2626" : "#0D9488"}
            />
          );
        })()}
        {rumSummary != null && rumSummary.pageviews_24h > 0 && (
          <StatCard
            label="RUM (24h pageviews)"
            value={rumSummary.pageviews_24h.toLocaleString()}
            Icon={BarChart2}
            color={rumSummary.p75_lcp_ms == null ? "#0D9488"
              : rumSummary.p75_lcp_ms <= 2500 ? "#16a34a"
              : rumSummary.p75_lcp_ms <= 4000 ? "#d97706"
              : "#dc2626"}
            subtitle={rumSummary.p75_lcp_ms != null ? `LCP p75 ${Math.round(rumSummary.p75_lcp_ms)}ms` : undefined}
          />
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title={t("panels.incident_trend")}>
          {data.trend.length === 0 ? (
            <EmptyState text={t("empty.no_incident_trend")} />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data.trend}
                  margin={{ top: 10, right: 10, left: -4, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#0D9488"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title={t("panels.monitor_status")}>
          {data.statusBreakdown.length === 0 ? (
            <EmptyState text={t("empty.no_monitor_status")} />
          ) : (
            <div className="grid gap-4 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.statusBreakdown}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={56}
                      outerRadius={92}
                      paddingAngle={3}
                    >
                      {data.statusBreakdown.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {data.statusBreakdown.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
        <Panel title={t("panels.incident_severity")}>
          {data.severityBreakdown.every((item) => item.count === 0) ? (
            <EmptyState text={t("empty.no_recent_incidents")} />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.severityBreakdown}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="severity"
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {data.severityBreakdown.map((entry) => (
                      <Cell key={entry.severity} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title={t("panels.monitors_needing_attention")}>
          {data.noisyMonitors.length === 0 ? (
            <EmptyState text={t("empty.no_monitors_needing_attention")} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {data.noisyMonitors.map((monitor) => (
                <Card key={monitor.id} className="py-3 px-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {monitor.name}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{monitor.target}</p>
                    </div>
                    <Badge color={STATUS_BADGE_COLOR[monitor.status] ?? "gray"}>
                      {t(`status.${monitor.status}`)}
                    </Badge>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>{monitor.type}</span>
                    <span>
                      {t("locations", { count: monitor.locations?.length || 0 })}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title={t("panels.recent_incidents")}>
        {incidents.length === 0 ? (
          <EmptyState text={t("empty.no_incidents_yet")} />
        ) : (
          <>
            {/* Mobile card list */}
            <div className="divide-y divide-gray-100 md:hidden">
              {incidents.map((incident) => {
                const started = new Date(incident.started_at);
                return (
                  <div key={incident.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                        {incident.monitor_name || incident.title}
                      </p>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1">
                        <Badge color={STATUS_BADGE_COLOR[incident.status] ?? "gray"}>
                          {t(`status.${incident.status}`)}
                        </Badge>
                        <Badge color={SEVERITY_BADGE_COLOR[incident.severity] ?? "gray"}>
                          {t(`severity.${incident.severity}`)}
                        </Badge>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">{fmtDateTime(started)}</p>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t("table.monitor")}</TableHeaderCell>
                    <TableHeaderCell>{t("table.started")}</TableHeaderCell>
                    <TableHeaderCell>{t("table.duration")}</TableHeaderCell>
                    <TableHeaderCell>{t("table.status")}</TableHeaderCell>
                    <TableHeaderCell>{t("table.severity")}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incidents.map((incident) => (
                    <IncidentRow key={incident.id} incident={incident} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 text-center text-sm text-gray-400">
      {text}
    </div>
  );
}
